import { memo, useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Pagination,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import PageLayout from "../../../../components/PageLayout";
import { useDocumentsList } from "../../hooks/useVerificationRequestList";
import { DataGrid } from "../../../ui/components/DataGrid";
import { FunctionViewField } from "../../../ui/components/viewField/FunctionViewField";
import { ListContainer } from "../../../ui/containers/ListContainer";
import { TId } from "../../../ui/models";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { TVerificationRequest } from "../../models";
import { getTitle } from "../../utils/getTitle";
import { getDocumentIcon } from "../../utils/getDocumentIcon";
import { getCategoryColor } from "../../utils/getCategory";
import { HeadButtons } from "../../../../components/HeadButtons";
import { SearchInput } from "../../../../components/SearchInput";
import { useAuth } from "../../../../auth/useAuth";
import {
  AUTH_COMPANY_NAME,
  AUTH_COMPANY_PURPOSE,
  AUTH_COMPLIANCE_EMAIL,
} from "../../../company/models";
import { useVerificationContext } from "../../context/useVerification";
import { mainTheme } from "../../../../theme/mainTheme";
import { useGetCompanyVerificationStatuses } from "../../hooks/useGetCompanyVerificationStatuses";
import BusinessIcon from "@mui/icons-material/Business";
import { VerificationStatusBadge } from "../../components/VerificationStatusBadge";

const dateOptions: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "numeric",
  timeZone: "Europe/Berlin",
};

const timeOptions: Intl.DateTimeFormatOptions = {
  hour: "numeric",
  minute: "numeric",
  hour12: true,
  timeZone: "Europe/Berlin",
  timeZoneName: "short",
};

const relativeTimeFormatter = new Intl.RelativeTimeFormat("en", {
  numeric: "always",
});

const relativeTimeUnits: Array<{
  unit: Intl.RelativeTimeFormatUnit;
  seconds: number;
}> = [
  { unit: "year", seconds: 365 * 24 * 60 * 60 },
  { unit: "month", seconds: 30 * 24 * 60 * 60 },
  { unit: "week", seconds: 7 * 24 * 60 * 60 },
  { unit: "day", seconds: 24 * 60 * 60 },
  { unit: "hour", seconds: 60 * 60 },
  { unit: "minute", seconds: 60 },
  { unit: "second", seconds: 1 },
];

const formatRelativeTime = (date: Date): string => {
  const secondsFromNow = (date.getTime() - Date.now()) / 1000;
  const absSecondsFromNow = Math.abs(secondsFromNow);

  if (absSecondsFromNow < 45) {
    return "just now";
  }

  const unit =
    relativeTimeUnits.find(({ seconds }) => absSecondsFromNow >= seconds) ||
    relativeTimeUnits[relativeTimeUnits.length - 1];
  const value = Math.round(secondsFromNow / unit.seconds);

  return relativeTimeFormatter.format(value, unit.unit);
};

const DateTimeCell = ({ value }: { value?: string }) => {
  const [relativeTime, setRelativeTime] = useState("");
  const [tooltipOpen, setTooltipOpen] = useState(false);

  if (!value) {
    return <Typography variant='body2'>-</Typography>;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return <Typography variant='body2'>-</Typography>;
  }

  const handleTooltipOpen = () => {
    setRelativeTime(formatRelativeTime(date));
    setTooltipOpen(true);
  };

  return (
    <Tooltip
      title={relativeTime}
      open={tooltipOpen}
      onOpen={handleTooltipOpen}
      onClose={() => setTooltipOpen(false)}
      placement='top'
      arrow
    >
      <Stack minWidth='92px'>
        <Typography variant='body2'>
          {date.toLocaleDateString("en-US", dateOptions)}
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          {date.toLocaleTimeString("en-US", timeOptions)}
        </Typography>
      </Stack>
    </Tooltip>
  );
};

export const VerificationRequestListScreen = memo(() => {
  const [statusesConfig, statusesConfigLoading, statusesConfigError] =
    useGetCompanyVerificationStatuses();
  const [offset, setOffset] = useState(0);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [category, setCategory] = useState<string>("");
  const [firstRender, setFirstRender] = useState(true);
  const [query, setQuery] = useState<string>("");
  const [isAnotherCompany, setIsAnotherCompany] = useState(false);
  const { authorizedCompanyId, companies } = useAuth();
  const { inputValue } = useVerificationContext();
  const currentCompany = localStorage.getItem(AUTH_COMPANY_NAME);
  const navigate = useNavigate();
  const { pageNum } = useParams<{ pageNum: string }>();
  const limitOnPage = 50;
  const [data, loading, error, request] = useDocumentsList(
    offset,
    limitOnPage,
    authorizedCompanyId,
    statuses,
    query,
    category
  );
  const linkRef = useRef<any>(null);
  const location = useLocation();

  const onRowClick = useCallback(
    (id: TId, fromFilter = false) => {
      if (fromFilter) {
        if (!data?.items.some((item: any) => item.id === id)) {
          navigate(
            `/verification-requests/${id}?page=${pageNum}&anotherCompany`
          );
          return;
        }
      }
      navigate(`/verification-requests/${id}?page=${pageNum}`);
    },
    [navigate, pageNum, data]
  );

  useEffect(() => {
    if (firstRender) {
      setQuery(inputValue);
      setFirstRender(false);
    }
  }, [firstRender, inputValue]);

  const refresh = useCallback(
    (offset: number, statuses: string[], query: string, category: string) => {
      if (pageNum && +pageNum === 1) {
        request(offset, statuses, query, category);
      } else {
        navigate(`/verification-requests/page/${1}`, { replace: true });
      }
    },
    [request, pageNum, navigate]
  );

  useEffect(() => {
    if (!pageNum) {
      navigate(`/verification-requests/page/${1}`, { replace: true });
    } else {
      const test = async () => {
        const newOffset = limitOnPage * (+pageNum - 1);
        setOffset(newOffset);
          await request(newOffset, statuses, query, category);
        if (location.hash) {
          linkRef.current?.click();
          navigate(location.pathname, { replace: true });
        }
      };
      if (!firstRender) {
        test();
      }
    }
  }, [pageNum, query, request, statuses, category, navigate, firstRender]);

  useEffect(() => {
    const selectedCompany = companies?.items?.find(
      (company) => company.name === currentCompany
    );
    const purpose = localStorage.getItem(AUTH_COMPANY_PURPOSE);
    const complianceEmail = localStorage.getItem(AUTH_COMPLIANCE_EMAIL);
    if (selectedCompany?.purpose && selectedCompany.purpose !== purpose) {
      localStorage.setItem(AUTH_COMPANY_PURPOSE, selectedCompany.purpose);
    }
    if (
      selectedCompany?.complianceEmail &&
      selectedCompany.complianceEmail !== complianceEmail
    ) {
      localStorage.setItem(
        AUTH_COMPLIANCE_EMAIL,
        selectedCompany.complianceEmail
      );
    }
  }, [companies, currentCompany]);

  const anyError = error || statusesConfigError;

  return (
    <PageLayout
      title={`Verification Requests for ${currentCompany}`}
      loading={loading || statusesConfigLoading}
      empty={error === null && (data === null || data.items.length === 0)}
    >
      {location.hash ? (
        <a style={{ display: "none" }} href={location.hash} ref={linkRef}>
          invisible
        </a>
      ) : null}

      <Stack direction='row' spacing={2} justifyContent='end'>
        <HeadButtons
          statusesConfig={statusesConfig}
          firstRender={firstRender}
          onChangeFilter={setStatuses}
          onChangeCategoryFilter={setCategory}
          goToVerification={onRowClick}
          isAnotherCompany={isAnotherCompany}
          setIsAnotherCompany={setIsAnotherCompany}
          onReload={() => refresh(offset, statuses, query, category)}
        />
        <SearchInput onChangeValue={setQuery} />
      </Stack>
      {anyError !== null && (
        <Alert severity='error'>{anyError.toString()}</Alert>
      )}
      <Pagination
        count={Math.floor(data?.count / limitOnPage) + 1}
        page={Math.floor(offset / limitOnPage) + 1}
        onChange={(event: React.ChangeEvent<unknown>, value: number) => {
          navigate(`/verification-requests/page/${value}`, { replace: true });
        }}
        sx={{ mb: "16px" }}
      />
      <ListContainer
        // data={sortByDate(data?.items, "descending", "updatedAt")}
        data={data?.items}
        isLoading={loading || statusesConfigLoading}
      >
        <DataGrid onRowClick={onRowClick}>
          <FunctionViewField
            label='Name'
            source='name'
            render={(record: TVerificationRequest) => (
              <Box sx={{ scrollMarginTop: "180px" }} id={record.id}>
                <Stack direction='row' alignItems='center' spacing={0.5}>
                  {record.category === "entity" ? (
                    <BusinessIcon
                      sx={{
                        fontSize: 18,
                        color: getCategoryColor(record.category),
                      }}
                    />
                  ) : null}
                  <Typography variant='h6'>{getTitle(record.id, record)}</Typography>
                </Stack>
                <Typography variant='body2'>ID: {record.id}</Typography>
              </Box>
            )}
          />
          <FunctionViewField
            label='Documents'
            render={(record: TVerificationRequest) => (
              <Box>
                <Stack
                  flexWrap='wrap'
                  direction='row'
                  justifyContent='flex-start'
                  alignItems='center'
                  spacing={1}
                >
                  {record.documents &&
                    record.documents.map((x) =>
                      getDocumentIcon(x.document.documentType, {
                        sx: { color: mainTheme.palette.warning.main },
                      })
                    )}
                </Stack>
              </Box>
            )}
          />

          <FunctionViewField
            label='Status'
            source='status'
            render={(record: TVerificationRequest) => {
              return (
                <Stack
                  direction='row'
                  justifyContent='flex-start'
                  alignItems='center'
                  spacing={1}
                  minWidth={"200px"}
                >
                  <VerificationStatusBadge
                    status={record.status}
                    statusesConfig={statusesConfig}
                  />
                </Stack>
              );
            }}
          />
          <FunctionViewField
            label='Created at'
            source='createdAt'
            render={(record: TVerificationRequest) => (
              <DateTimeCell value={record.createdAt} />
            )}
          />
          <FunctionViewField
            label='Updated at'
            source='updatedAt'
            render={(record: TVerificationRequest) => (
              <DateTimeCell value={record.updatedAt} />
            )}
          />
          <FunctionViewField
            label='Decision date'
            source='decisionTime'
            render={(record: TVerificationRequest) => (
              <DateTimeCell value={record.decisionTime} />
            )}
          />
          <FunctionViewField
            label='App'
            source='appName'
            render={(record: TVerificationRequest) =>
              record.appName?.trim() || "-"
            }
          />
        </DataGrid>
        <Pagination
          count={Math.floor(data?.count / limitOnPage) + 1}
          page={Math.floor(offset / limitOnPage) + 1}
          onChange={(event: React.ChangeEvent<unknown>, value: number) => {
            navigate(`/verification-requests/page/${value}`, { replace: true });
          }}
          sx={{ mb: "16px" }}
        />
      </ListContainer>
    </PageLayout>
  );
});
